import React, { useEffect, useRef, useCallback, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import { cn } from "@/lib/utils";
import AfterTaxIncomeChart from '../../charts/AfterTaxIncomeChart';
import { RetirementFinancialInputs } from '../../Types/Autogenerated/AfterTaxRetirementIncomeOptimizerRequestPayload';
import { AfterTaxRetirementIncomeOptimizerAPIResponse } from '../../Types/Autogenerated/AfterTaxRetirementIncomeOptimizerApiResponse';
import { Convert as FilingStatusConvert } from '../../Types/Autogenerated/FilingStatus';
import { Convert as StateConvert } from '../../Types/Autogenerated/State';
import { FormData } from '../../App';
import { CurrencyInputOnChangeValues } from 'react-currency-input-field';
import { taxYearItems, stateItems, filingStatuses } from '../../Utils/Constants';
import TermsDescriptions from '../../components/TermsDescriptions';
import { afterTaxRetirementTerms } from '../../Utils/TermDefinitions';


interface AfterTaxRetirementIncomeOptimizerPageProps {
    formData: FormData;
    selectedPage: string; // Should always be 'After-Tax Retirement Income' when this component is displayed
    personsToggle: boolean;
    adjustYearlyForInflationToggle: boolean;
    showSnackbar: boolean;
    snackbarMessage: string;
    showChart: boolean;
    chartData: number[][] | null;
    buttonStyle: { margin: number; };
    handleClose: (event: React.SyntheticEvent | Event, reason?: string) => void;
    setChartData: (value: React.SetStateAction<number[][] | null>) => void;
    setShowChart: (value: React.SetStateAction<boolean>) => void;
    handleClick: () => void;
    // Set error messges in the snackbar
    setSnackbarMessage: (value: React.SetStateAction<string>) => void;
    // Primary handler for handling the change in an input value
    handleInputChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    // onValueChange is used instead of onChange because of how the user-input is modified by CurrencyInput,
    // which seems to cause onChange to not always be triggered appriopriately as commas are shifted around
    onValueChange: (value: string | undefined, name?: string, values?: CurrencyInputOnChangeValues) => void;
    // Hanlder for when a toggle is changed
    handleToggleChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    // Hanlder for when a select is changed
    handleSelectChange: (event: React.ChangeEvent<HTMLSelectElement>) => void;
  }
    

const AfterTaxRetirementIncomeOptimizerPage: React.FC<AfterTaxRetirementIncomeOptimizerPageProps> = ({ 
    formData, 
    selectedPage,
    personsToggle, 
    adjustYearlyForInflationToggle, 
    showSnackbar,
    snackbarMessage,
    showChart,
    chartData,
    buttonStyle,
    handleClose,
    setChartData,
    setShowChart,
    handleClick,
    setSnackbarMessage, 
    handleInputChange, 
    onValueChange, 
    handleToggleChange,
    handleSelectChange,
}) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(false);

  const formatNumber = (num: number | string) => {
    if (typeof num === 'string') {
      num = parseFloat(num.replace(/,/g, ''));
    }
    return isNaN(num) ? '' : num.toLocaleString('en-US');
  };

  const handleFinancialInputChange = (value: string, name: string) => {
    const numericValue = value ? parseFloat(value.replace(/,/g, '')) : 0;
    onValueChange(numericValue.toString(), name);
  };

  // Function to render inputs based on the number of persons
  const renderInputs = () => {
    return (
      <div className={cn("space-y-4 pb-4")}>
        <div className={cn("flex items-center justify-between")}>
          <Label htmlFor="personsToggle" className="text-left w-full">Two Incomes</Label>
          <Switch
            id="personsToggle"
            checked={personsToggle}
            onCheckedChange={(checked) => handleToggleChange({
              target: { name: 'personsToggle', checked, id: 'persons', type: 'checkbox' }
            } as React.ChangeEvent<HTMLInputElement>)}
          />
        </div>

        <div className={cn("grid gap-4", personsToggle ? "grid-cols-2" : "grid-cols-1")}>
          <div className={cn("space-y-4")}>
            {renderFinancialInput(`Trad Balance${personsToggle ? ' 1' : ''}`, "initialTradBalance", personsToggle ? "initialTradBalancePerson1" : "initialTradBalance")}
            {renderFinancialInput(`Roth Balance${personsToggle ? ' 1' : ''}`, "initialRothBalance", personsToggle ? "initialRothBalancePerson1" : "initialRothBalance")}
            {renderFinancialInput(`Brokerage Balance${personsToggle ? ' 1' : ''}`, "initialBrokerageBalance", personsToggle ? "initialBrokerageBalancePerson1" : "initialBrokerageBalance")}
            {renderFinancialInput(`Yearly Trad 401k${personsToggle ? ' 1' : ''}`, "yearlyTrad401k", personsToggle ? "yearlyTrad401kPerson1" : "yearlyTrad401k")}
            {renderFinancialInput(`Yearly Roth 401k${personsToggle ? ' 1' : ''}`, "yearlyRoth401k", personsToggle ? "yearlyRoth401kPerson1" : "yearlyRoth401k")}
            {renderFinancialInput(`Yearly 401k Match${personsToggle ? ' 1' : ''}`, "yearlyTrad401kCompanyMatch", personsToggle ? "yearlyTrad401kCompanyMatchPerson1" : "yearlyTrad401kCompanyMatch")}
            {renderFinancialInput(`Yearly AfterTax 401k${personsToggle ? ' 1' : ''}`, "yearlyAfterTax401k", personsToggle ? "yearlyAfterTax401kPerson1" : "yearlyAfterTax401k")}
            {renderFinancialInput(`Yearly Trad IRA${personsToggle ? ' 1' : ''}`, "yearlyTradIRA", personsToggle ? "yearlyTradIRAPerson1" : "yearlyTradIRA")}
            {renderFinancialInput(`Yearly Roth IRA${personsToggle ? ' 1' : ''}`, "yearlyRothIRA", personsToggle ? "yearlyRothIRAPerson1" : "yearlyRothIRA")}
            {renderFinancialInput(`Yearly Brokerage${personsToggle ? ' 1' : ''}`, "yearlyBrokerage", personsToggle ? "yearlyBrokeragePerson1" : "yearlyBrokerage")}
            {renderFinancialInput(`Income${personsToggle ? ' 1' : ''}`, "income", personsToggle ? "incomePerson1" : "income")}
            {renderNumberInput(`Current Age${personsToggle ? ' 1' : ''}`, "currentAge", personsToggle ? "currentAgePerson1" : "currentAge")}
            {renderNumberInput(`Stop Savings Age${personsToggle ? ' 1' : ''}`, "contributionsEndAge", personsToggle ? "contributionsEndAgePerson1" : "contributionsEndAge")}
            {renderNumberInput(`Retirement Age${personsToggle ? ' 1' : ''}`, "withdrawalStartAge", personsToggle ? "withdrawalStartAgePerson1" : "withdrawalStartAge")}
          </div>

          {personsToggle && (
            <div className={cn("space-y-4")}>
              {renderFinancialInput("Trad Balance 2", "initialTradBalancePerson2", "initialTradBalancePerson2")}
              {renderFinancialInput("Roth Balance 2", "initialRothBalancePerson2", "initialRothBalancePerson2")}
              {renderFinancialInput("Brokerage Balance 2", "initialBrokerageBalancePerson2", "initialBrokerageBalancePerson2")}
              {renderFinancialInput("Yearly Trad 401k 2", "yearlyTrad401kPerson2", "yearlyTrad401kPerson2")}
              {renderFinancialInput("Yearly Roth 401k 2", "yearlyRoth401kPerson2", "yearlyRoth401kPerson2")}
              {renderFinancialInput("Yearly 401k Match 2", "yearlyTrad401kCompanyMatchPerson2", "yearlyTrad401kCompanyMatchPerson2")}
              {renderFinancialInput("Yearly AfterTax 401k 2", "yearlyAfterTax401kPerson2", "yearlyAfterTax401kPerson2")}
              {renderFinancialInput("Yearly Trad IRA 2", "yearlyTradIRAPerson2", "yearlyTradIRAPerson2")}
              {renderFinancialInput("Yearly Roth IRA 2", "yearlyRothIRAPerson2", "yearlyRothIRAPerson2")}
              {renderFinancialInput("Yearly Brokerage 2", "yearlyBrokeragePerson2", "yearlyBrokeragePerson2")}
              {renderFinancialInput("Income 2", "incomePerson2", "incomePerson2")}
              {renderNumberInput("Current Age 2", "currentAgePerson2", "currentAgePerson2")}
              {renderNumberInput("Stop Savings Age 2", "contributionsEndAgePerson2", "contributionsEndAgePerson2")}
              {renderNumberInput("Retirement Age 2", "withdrawalStartAgePerson2", "withdrawalStartAgePerson2")}
            </div>
          )}
        </div>

        <div className={cn("flex items-center justify-between")}>
          <Label htmlFor="adjustYearlyForInflationToggle" className="text-left w-full">Grow Contributions With Inflation</Label>
          <Switch
            id="adjustYearlyForInflationToggle"
            checked={adjustYearlyForInflationToggle}
            onCheckedChange={(checked) => handleToggleChange({
              target: { name: 'adjustYearlyForInflationToggle', checked, id: 'adjustYearlyForInflationToggle', type: 'checkbox' }
            } as React.ChangeEvent<HTMLInputElement>)}
          />
        </div>

        {renderPercentageInput("Withdrawal Rate", "withdrawalRate")}
        {renderPercentageInput("Average Annual Return", "averageAnnualReturn")}
        {renderPercentageInput("Inflation Rate", "inflationRate")}

        {renderDropdown("Tax Year", "taxYear", taxYearItems)}
        {renderDropdown("State", "state", stateItems)}
        {renderDropdown("Filing Status", "filingStatus", filingStatuses)}

      </div>
    );
  };

  const renderFinancialInput = (label: string, name: string, formDataKey: string) => (
    <div>
      <Label htmlFor={name} className="text-left w-full">{label}</Label>
      <div className={cn("relative")}>
        <span className={cn("absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500 text-base md:text-sm")}>$</span>
        <Input
          id={name}
          name={formDataKey}
          type="text"
          value={formatNumber(String(formData[formDataKey as keyof FormData]))}
          onChange={(e) => handleFinancialInputChange(e.target.value, formDataKey)}
          className={cn("pl-6")}
        />
      </div>
    </div>
  );

  const renderNumberInput = (label: string, name: string, formDataKey: string) => (
    <div>
      <Label htmlFor={name} className="text-left w-full">{label}</Label>
      <Input
        id={name}
        name={formDataKey}
        type="number"
        value={String(formData[formDataKey as keyof FormData])}
        onChange={handleInputChange}
      />
    </div>
  );

  const renderPercentageInput = (label: string, name: string) => (
    <div>
      <Label htmlFor={name} className="text-left w-full">{label}</Label>
      <div className={cn("relative")}>
        <Input
          id={name}
          name={name}
          type="number"
          value={String(formData[name as keyof FormData])}
          onChange={handleInputChange}
          className={cn("pr-8")}
        />
        <span className={cn("absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 text-base md:text-sm")}>%</span>
      </div>
    </div>
  );

  const sortedTaxYears = Object.entries(taxYearItems)
    .sort((a, b) => parseInt(b[1]) - parseInt(a[1]));

  const renderDropdown = (label: string, name: string, options: Record<string, string>) => (
    <div>
      <Label htmlFor={name} className="text-left w-full">{label}</Label>
      <Select 
        value={formData[name as keyof FormData] as string}
        onValueChange={(value) => handleSelectChange({ target: { name, value } } as React.ChangeEvent<HTMLSelectElement>)}
      >
        <SelectTrigger id={name} name={name}>
          <SelectValue placeholder={`Select ${label}`} />
        </SelectTrigger>
        <SelectContent>
          {name === 'taxYear'
            ? sortedTaxYears.map(([key, value]) => (
                <SelectItem key={value} value={value}>{key}</SelectItem>
              ))
            : Object.entries(options).map(([value, label]) => (
                <SelectItem key={value} value={value}>{label}</SelectItem>
              ))
            }
        </SelectContent>
      </Select>
    </div>
  );

  const labels: string[] = [
    "Income Roth",
    "Income Trad",
    "Income Brokerage",
    "Income Post Tax"
  ];  

  // Function to fetch and parse the data
  const fetchData = useCallback(async (url: string, fetchOptions: any): Promise<AfterTaxRetirementIncomeOptimizerAPIResponse> => {
    const response = await fetch(url, fetchOptions);
    if (!response.ok) {
      const errorData = await response.json();
      const errorMessage: string = errorData.error || 'Unknown error occurred'
      setSnackbarMessage(errorMessage)
      throw new Error(errorMessage);
    }

    const data = await response.json();
    return data as AfterTaxRetirementIncomeOptimizerAPIResponse;
  }, [setSnackbarMessage]);
  const makeAfterTaxIncomeOptimizerUrl = () => {
    // For local development, use localhost
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      return 'http://127.0.0.1:5000/RetirementOptimizer';
    }
    
    // For deployed environments, use the CloudFront API path
    return `https://${window.location.hostname}/api/retirement-optimizer`;
  }


  // Memoize input data to prevent unnecessary recalculations
  const inputData = useMemo(() => {
    const singlePersonData = {
      initialRothBalance: formData.initialRothBalance,
      initialTradBalance: formData.initialTradBalance,
      initialBrokerageBalance: formData.initialBrokerageBalance,
      yearlyRoth401k: formData.yearlyRoth401k,
      yearlyTrad401k: formData.yearlyTrad401k,
      yearlyTrad401kCompanyMatch: formData.yearlyTrad401kCompanyMatch,
      yearlyAfterTax401k: formData.yearlyAfterTax401k,
      yearlyRothIRA: formData.yearlyRothIRA,
      yearlyTradIRA: formData.yearlyTradIRA,
      yearlyBrokerage: formData.yearlyBrokerage,
      currentAge: formData.currentAge,
      contributionsEndAge: formData.contributionsEndAge,
      withdrawalStartAge: formData.withdrawalStartAge,
      income: formData.income,
    };

    const twoPersonData = {
      person1: {
        initialRothBalance: formData.initialRothBalancePerson1,
        initialTradBalance: formData.initialTradBalancePerson1,
        initialBrokerageBalance: formData.initialBrokerageBalancePerson1,
        yearlyRoth401k: formData.yearlyRoth401kPerson1,
        yearlyTrad401k: formData.yearlyTrad401kPerson1,
        yearlyTrad401kCompanyMatch: formData.yearlyTrad401kCompanyMatchPerson1,
        yearlyAfterTax401k: formData.yearlyAfterTax401kPerson1,
        yearlyRothIRA: formData.yearlyRothIRAPerson1,
        yearlyTradIRA: formData.yearlyTradIRAPerson1,
        yearlyBrokerage: formData.yearlyBrokeragePerson1,
        currentAge: formData.currentAgePerson1,
        contributionsEndAge: formData.contributionsEndAgePerson1,
        withdrawalStartAge: formData.withdrawalStartAgePerson1,
        income: formData.incomePerson1,
      },
      person2: {
        initialRothBalance: formData.initialRothBalancePerson2,
        initialTradBalance: formData.initialTradBalancePerson2,
        initialBrokerageBalance: formData.initialBrokerageBalancePerson2,
        yearlyRoth401k: formData.yearlyRoth401kPerson2,
        yearlyTrad401k: formData.yearlyTrad401kPerson2,
        yearlyTrad401kCompanyMatch: formData.yearlyTrad401kCompanyMatchPerson2,
        yearlyAfterTax401k: formData.yearlyAfterTax401kPerson2,
        yearlyRothIRA: formData.yearlyRothIRAPerson2,
        yearlyTradIRA: formData.yearlyTradIRAPerson2,
        yearlyBrokerage: formData.yearlyBrokeragePerson2,
        currentAge: formData.currentAgePerson2,
        contributionsEndAge: formData.contributionsEndAgePerson2,
        withdrawalStartAge: formData.withdrawalStartAgePerson2,
        income: formData.incomePerson2,
      }
    };

    return {
      personsToggle,
      adjustYearlyForInflationToggle,
      singlePersonData,
      twoPersonData,
      filingStatus: formData.filingStatus,
      inflationRate: formData.inflationRate,
      averageAnnualReturn: formData.averageAnnualReturn,
      state: formData.state,
      taxYear: formData.taxYear,
      withdrawalRate: formData.withdrawalRate,
    };
  }, [
    personsToggle,
    adjustYearlyForInflationToggle,
    // Single person data
    formData.initialRothBalance,
    formData.initialTradBalance,
    formData.initialBrokerageBalance,
    formData.yearlyRoth401k,
    formData.yearlyTrad401k,
    formData.yearlyTrad401kCompanyMatch,
    formData.yearlyAfterTax401k,
    formData.yearlyRothIRA,
    formData.yearlyTradIRA,
    formData.yearlyBrokerage,
    formData.currentAge,
    formData.contributionsEndAge,
    formData.withdrawalStartAge,
    formData.income,
    // Two person data
    formData.initialRothBalancePerson1,
    formData.initialTradBalancePerson1,
    formData.initialBrokerageBalancePerson1,
    formData.yearlyRoth401kPerson1,
    formData.yearlyTrad401kPerson1,
    formData.yearlyTrad401kCompanyMatchPerson1,
    formData.yearlyAfterTax401kPerson1,
    formData.yearlyRothIRAPerson1,
    formData.yearlyTradIRAPerson1,
    formData.yearlyBrokeragePerson1,
    formData.currentAgePerson1,
    formData.contributionsEndAgePerson1,
    formData.withdrawalStartAgePerson1,
    formData.incomePerson1,
    formData.initialRothBalancePerson2,
    formData.initialTradBalancePerson2,
    formData.initialBrokerageBalancePerson2,
    formData.yearlyRoth401kPerson2,
    formData.yearlyTrad401kPerson2,
    formData.yearlyTrad401kCompanyMatchPerson2,
    formData.yearlyAfterTax401kPerson2,
    formData.yearlyRothIRAPerson2,
    formData.yearlyTradIRAPerson2,
    formData.yearlyBrokeragePerson2,
    formData.currentAgePerson2,
    formData.contributionsEndAgePerson2,
    formData.withdrawalStartAgePerson2,
    formData.incomePerson2,
    // Common data
    formData.filingStatus,
    formData.inflationRate,
    formData.averageAnnualReturn,
    formData.state,
    formData.taxYear,
    formData.withdrawalRate,
  ]);

  // Create the payload from memoized input data
  const payload = useMemo(() => {
    const retirementFinancialInputs: RetirementFinancialInputs[] = !inputData.personsToggle
      ? [{
          initial_roth_balance: parseFloat(inputData.singlePersonData.initialRothBalance),
          initial_trad_balance: parseFloat(inputData.singlePersonData.initialTradBalance),
          initial_brokerage_balance: parseFloat(inputData.singlePersonData.initialBrokerageBalance),
          yearly_roth_401k: parseFloat(inputData.singlePersonData.yearlyRoth401k),
          yearly_trad_401k: parseFloat(inputData.singlePersonData.yearlyTrad401k),
          yearly_trad_401k_company_match: parseFloat(inputData.singlePersonData.yearlyTrad401kCompanyMatch),
          yearly_after_tax_401k: parseFloat(inputData.singlePersonData.yearlyAfterTax401k),
          yearly_roth_ira: parseFloat(inputData.singlePersonData.yearlyRothIRA),
          yearly_trad_ira: parseFloat(inputData.singlePersonData.yearlyTradIRA),
          yearly_brokerage: parseFloat(inputData.singlePersonData.yearlyBrokerage),
          current_age: parseInt(inputData.singlePersonData.currentAge),
          contributions_end_age: parseInt(inputData.singlePersonData.contributionsEndAge),
          withdrawal_start_age: parseInt(inputData.singlePersonData.withdrawalStartAge),
          income: parseFloat(inputData.singlePersonData.income),
        }]
      : [{
          initial_roth_balance: parseFloat(inputData.twoPersonData.person1.initialRothBalance),
          initial_trad_balance: parseFloat(inputData.twoPersonData.person1.initialTradBalance),
          initial_brokerage_balance: parseFloat(inputData.twoPersonData.person1.initialBrokerageBalance),
          yearly_roth_401k: parseFloat(inputData.twoPersonData.person1.yearlyRoth401k),
          yearly_trad_401k: parseFloat(inputData.twoPersonData.person1.yearlyTrad401k),
          yearly_trad_401k_company_match: parseFloat(inputData.twoPersonData.person1.yearlyTrad401kCompanyMatch),
          yearly_after_tax_401k: parseFloat(inputData.twoPersonData.person1.yearlyAfterTax401k),
          yearly_roth_ira: parseFloat(inputData.twoPersonData.person1.yearlyRothIRA),
          yearly_trad_ira: parseFloat(inputData.twoPersonData.person1.yearlyTradIRA),
          yearly_brokerage: parseFloat(inputData.twoPersonData.person1.yearlyBrokerage),
          current_age: parseInt(inputData.twoPersonData.person1.currentAge),
          contributions_end_age: parseInt(inputData.twoPersonData.person1.contributionsEndAge),
          withdrawal_start_age: parseInt(inputData.twoPersonData.person1.withdrawalStartAge),
          income: parseFloat(inputData.twoPersonData.person1.income),
        }, {
          initial_roth_balance: parseFloat(inputData.twoPersonData.person2.initialRothBalance),
          initial_trad_balance: parseFloat(inputData.twoPersonData.person2.initialTradBalance),
          initial_brokerage_balance: parseFloat(inputData.twoPersonData.person2.initialBrokerageBalance),
          yearly_roth_401k: parseFloat(inputData.twoPersonData.person2.yearlyRoth401k),
          yearly_trad_401k: parseFloat(inputData.twoPersonData.person2.yearlyTrad401k),
          yearly_trad_401k_company_match: parseFloat(inputData.twoPersonData.person2.yearlyTrad401kCompanyMatch),
          yearly_after_tax_401k: parseFloat(inputData.twoPersonData.person2.yearlyAfterTax401k),
          yearly_roth_ira: parseFloat(inputData.twoPersonData.person2.yearlyRothIRA),
          yearly_trad_ira: parseFloat(inputData.twoPersonData.person2.yearlyTradIRA),
          yearly_brokerage: parseFloat(inputData.twoPersonData.person2.yearlyBrokerage),
          current_age: parseInt(inputData.twoPersonData.person2.currentAge),
          contributions_end_age: parseInt(inputData.twoPersonData.person2.contributionsEndAge),
          withdrawal_start_age: parseInt(inputData.twoPersonData.person2.withdrawalStartAge),
          income: parseFloat(inputData.twoPersonData.person2.income),
        }];

    return {
      adjust_yearly_for_inflation: inputData.adjustYearlyForInflationToggle,
      filing_status: FilingStatusConvert.toFilingStatus(JSON.stringify(inputData.filingStatus)),
      inflation: parseFloat(inputData.inflationRate) / 100,
      interest: parseFloat(inputData.averageAnnualReturn) / 100,
      retirement_financial_inputs: retirementFinancialInputs,
      state: StateConvert.toState(JSON.stringify(inputData.state)),
      tax_year: parseInt(inputData.taxYear),
      withdrawal_rate: parseFloat(inputData.withdrawalRate) / 100,
    };
  }, [inputData]);

  const processChartData = useCallback((data: AfterTaxRetirementIncomeOptimizerAPIResponse): number[][] => {
    // Process the data to a format suitable for your chart
    
    // Extract data for the graph
    // Extract the relevant labels from the API response
    const chartData = [
      [
        data.current.income_roth,
        data.current.income_trad,
        data.current.income_brokerage,
        data.current.income_post_tax,
      ],
      [
        data.optimized.income_roth,
        data.optimized.income_trad,
        data.optimized.income_brokerage,
        data.optimized.income_post_tax,
      ]
    ];

  return chartData
  }, []);

  const isLoadingRef = useRef(false);

  const makeAPIRequest = useCallback(async () => {
    if (isLoadingRef.current) return; // Prevent multiple simultaneous requests
    
    isLoadingRef.current = true;
    setIsLoading(true);
    try {
      const url = makeAfterTaxIncomeOptimizerUrl();
      const fetchOptions = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      };

      const data = await fetchData(url, fetchOptions);
      setChartData(processChartData(data));
      setShowChart(true);
    } catch (error) {
      console.error('Error:', error);
      handleClick();
    } finally {
      isLoadingRef.current = false;
      setIsLoading(false);
    }
  }, [payload, fetchData, processChartData, setChartData, setShowChart, handleClick]);

  // Track previous input data for change detection
  const previousInputDataRef = useRef<string>('');

  // Auto-calculate with debouncing for larger API calls - only when inputs actually change
  useEffect(() => {
    const currentInputDataString = JSON.stringify(inputData);
    
    // Only make API request if input data has actually changed
    if (currentInputDataString !== previousInputDataRef.current) {
      const timeoutId = setTimeout(() => {
        makeAPIRequest();
        previousInputDataRef.current = currentInputDataString;
      }, 1000); // 1000ms debounce for larger API calls

      return () => clearTimeout(timeoutId);
    }
  }, [inputData, makeAPIRequest]);

  return (
    <div className="after-tax-retirement-income-optimizer w-full px-1 sm:px-2 lg:px-3 h-full flex flex-col">
      <div className="flex flex-col lg:flex-row gap-4 h-full mt-16 sm:mt-20 md:mt-24 mb-4">
        {/* Form Section */}
        <div className="lg:w-1/3 min-w-0">
          <Card className="w-full flex-grow overflow-hidden flex flex-col">
            <CardHeader>
              <h2 className={cn("text-2xl font-bold")}>{selectedPage}</h2>
            </CardHeader>
            <CardContent className="flex-grow overflow-y-auto">
              {renderInputs()}
            </CardContent>
          </Card>
        </div>

        {/* Chart Section */}
        <div className="lg:w-2/3 min-w-0" ref={chartRef}>
          <Card className="w-full lg:h-[calc(100vh-200px)] h-auto">
            <CardContent className="p-4 h-full">
              {chartData && !isLoading ? (
                <div className="h-full flex flex-col">
                  <div className="flex-grow min-h-0">
                    <AfterTaxIncomeChart labels={labels} chartData={chartData} />
                  </div>
                </div>
              ) : (
                <div className="h-full flex items-center justify-center min-h-[400px]">
                  <div className="flex flex-col items-center gap-2">
                    {isLoading && (
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    )}
                    <div className="text-gray-500">
                      {isLoading ? 'Calculating optimization...' : 'Loading chart...'}
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Terms Descriptions Section */}
      <TermsDescriptions 
        terms={afterTaxRetirementTerms} 
        title="After-Tax Retirement Income Optimizer Terms" 
      />
      
      {showSnackbar && (
        <div className={cn("fixed bottom-4 left-4 right-4 bg-red-500 text-white p-4 rounded-md")}>
          {snackbarMessage}
        </div>
      )}
    </div>
  );
};

export default AfterTaxRetirementIncomeOptimizerPage;
